/**
 * Session Management Utility
 * Implements secure session handling with configurable timeouts and activity tracking
 */

import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '../firebase';
import { logAuthEvent, EVENT_TYPES } from './securityLogger';

// Session configuration
export const SESSION_CONFIG = {
  TIMEOUT_DURATION: 2 * 30 * 24 * 60 * 60 * 1000, // 2 months in milliseconds
  ACTIVITY_CHECK_INTERVAL: 5 * 60 * 1000, // Check every 5 minutes
  WARNING_DURATION: 7 * 24 * 60 * 60 * 1000, // Show warning 7 days before timeout
  STORAGE_KEY: 'dp_duel_session_data'
};

class SessionManager {
  constructor() {
    this.sessionData = null;
    this.activityTimer = null;
    this.warningTimer = null;
    this.isInitialized = false;
    this.lastActivity = null;
    this.sessionStartTime = null;
    
    this.init();
  }

  /**
   * Initialize session management
   */
  init() {
    if (this.isInitialized) return;
    
    this.loadSessionData();
    this.setupActivityTracking();
    this.setupAuthStateListener();
    this.isInitialized = true;
    
    console.log('🔐 Session Manager initialized');
  }

  /**
   * Load session data from localStorage
   */
  loadSessionData() {
    try {
      const stored = localStorage.getItem(SESSION_CONFIG.STORAGE_KEY);
      if (stored) {
        this.sessionData = JSON.parse(stored);
        this.sessionStartTime = this.sessionData.sessionStartTime;
        this.lastActivity = this.sessionData.lastActivity;
      }
    } catch (error) {
      console.error('❌ Failed to load session data:', error);
      this.clearSessionData();
    }
  }

  /**
   * Save session data to localStorage
   */
  saveSessionData() {
    try {
      const data = {
        sessionStartTime: this.sessionStartTime,
        lastActivity: this.lastActivity,
        version: '1.0'
      };
      localStorage.setItem(SESSION_CONFIG.STORAGE_KEY, JSON.stringify(data));
    } catch (error) {
      console.error('❌ Failed to save session data:', error);
    }
  }

  /**
   * Clear session data
   */
  clearSessionData() {
    try {
      localStorage.removeItem(SESSION_CONFIG.STORAGE_KEY);
      this.sessionData = null;
      this.sessionStartTime = null;
      this.lastActivity = null;
    } catch (error) {
      console.error('❌ Failed to clear session data:', error);
    }
  }

  /**
   * Setup activity tracking for user interactions
   */
  setupActivityTracking() {
    // Track various user activities
    const activities = [
      'mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'
    ];

    const resetActivity = () => {
      this.lastActivity = Date.now();
      this.saveSessionData();
      this.resetWarningTimer();
    };

    // Add event listeners for activity tracking
    activities.forEach(activity => {
      document.addEventListener(activity, resetActivity, { passive: true });
    });

    // Start the activity monitoring timer
    this.startActivityTimer();
  }

  /**
   * Setup Firebase auth state listener
   */
  setupAuthStateListener() {
    onAuthStateChanged(auth, (user) => {
      if (user) {
        // User is signed in
        if (!this.sessionStartTime) {
          this.sessionStartTime = Date.now();
          this.lastActivity = Date.now();
          this.saveSessionData();
          console.log('🔐 New session started');
        }
        this.startActivityTimer();
      } else {
        // User is signed out
        this.clearSession();
      }
    });
  }

  /**
   * Start activity monitoring timer
   */
  startActivityTimer() {
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
    }

    this.activityTimer = setInterval(() => {
      this.checkSessionValidity();
    }, SESSION_CONFIG.ACTIVITY_CHECK_INTERVAL);
  }

  /**
   * Check if session is still valid
   */
  checkSessionValidity() {
    if (!auth.currentUser || !this.lastActivity) {
      return;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;
    const timeSinceSessionStart = now - this.sessionStartTime;

    // Check if session has expired
    if (timeSinceLastActivity > SESSION_CONFIG.TIMEOUT_DURATION) {
      console.log('🔐 Session expired due to inactivity');
      this.handleSessionExpiry('inactivity');
      return;
    }

    // Check if session has exceeded maximum duration (2 months from start)
    if (timeSinceSessionStart > SESSION_CONFIG.TIMEOUT_DURATION) {
      console.log('🔐 Session expired due to maximum duration');
      this.handleSessionExpiry('max_duration');
      return;
    }

    // Check if we should show warning
    const timeUntilExpiry = SESSION_CONFIG.TIMEOUT_DURATION - timeSinceLastActivity;
    if (timeUntilExpiry <= SESSION_CONFIG.WARNING_DURATION && !this.warningTimer) {
      this.showSessionWarning(timeUntilExpiry);
    }
  }

  /**
   * Show session warning to user
   */
  showSessionWarning(timeUntilExpiry) {
    const daysLeft = Math.ceil(timeUntilExpiry / (24 * 60 * 60 * 1000));
    
    // Log warning event
    logAuthEvent(EVENT_TYPES.SESSION_WARNING, auth.currentUser?.uid, {
      daysRemaining: daysLeft,
      warningType: 'session_timeout'
    });

    // Show warning to user (you can customize this UI)
    console.warn(`⚠️ Session will expire in ${daysLeft} days due to inactivity`);
    
    // You can show a toast notification or modal here
    if (window.showSessionWarning) {
      window.showSessionWarning(daysLeft);
    }

    this.warningTimer = setTimeout(() => {
      this.warningTimer = null;
    }, 24 * 60 * 60 * 1000); // Reset warning after 24 hours
  }

  /**
   * Reset warning timer
   */
  resetWarningTimer() {
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Handle session expiry
   */
  async handleSessionExpiry(reason) {
    try {
      const userId = auth.currentUser?.uid;
      
      // Log session expiry
      await logAuthEvent(EVENT_TYPES.SESSION_EXPIRED, userId, {
        reason: reason,
        sessionDuration: Date.now() - this.sessionStartTime,
        lastActivity: this.lastActivity
      });

      // Clear session data
      this.clearSession();

      // Sign out user
      await signOut(auth);

      // Redirect to login page
      if (window.location.pathname !== '/login') {
        window.location.href = '/login?reason=session_expired';
      }

    } catch (error) {
      console.error('❌ Error handling session expiry:', error);
    }
  }

  /**
   * Clear session and cleanup
   */
  clearSession() {
    if (this.activityTimer) {
      clearInterval(this.activityTimer);
      this.activityTimer = null;
    }
    
    this.resetWarningTimer();
    this.clearSessionData();
    
    console.log('🔐 Session cleared');
  }

  /**
   * Get session information
   */
  getSessionInfo() {
    if (!this.sessionStartTime || !this.lastActivity) {
      return null;
    }

    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivity;
    const timeSinceSessionStart = now - this.sessionStartTime;
    const timeUntilExpiry = SESSION_CONFIG.TIMEOUT_DURATION - timeSinceLastActivity;

    return {
      sessionStartTime: this.sessionStartTime,
      lastActivity: this.lastActivity,
      timeSinceLastActivity,
      timeSinceSessionStart,
      timeUntilExpiry,
      isExpired: timeSinceLastActivity > SESSION_CONFIG.TIMEOUT_DURATION,
      daysUntilExpiry: Math.ceil(timeUntilExpiry / (24 * 60 * 60 * 1000))
    };
  }

  /**
   * Force session refresh (reset timers)
   */
  refreshSession() {
    this.lastActivity = Date.now();
    this.saveSessionData();
    this.resetWarningTimer();
    console.log('🔐 Session refreshed');
  }

  /**
   * Check if user is currently active
   */
  isUserActive() {
    if (!this.lastActivity) return false;
    const timeSinceLastActivity = Date.now() - this.lastActivity;
    return timeSinceLastActivity < (5 * 60 * 1000); // Active if last activity within 5 minutes
  }
}

// Create singleton instance
const sessionManager = new SessionManager();

// Export functions for easy use
export const initializeSession = () => sessionManager.init();
export const refreshSession = () => sessionManager.refreshSession();
export const getSessionInfo = () => sessionManager.getSessionInfo();
export const isUserActive = () => sessionManager.isUserActive();
export const clearSession = () => sessionManager.clearSession();

// Export the class for advanced usage
export default sessionManager;
